import React, { useState, useMemo } from 'react';
import { Heart, Copy, Trash2, Sparkles, Check, Search, ChevronDown, ChevronUp, Globe, Pencil, X } from 'lucide-react';
import { toast } from 'sonner';
import { useData } from '../contexts/DataContext';

const MARKET_FLAGS  = { SE: '🇸🇪', NO: '🇳🇴', FI: '🇫🇮', DE: '🇩🇪', DK: '🇩🇰', NL: '🇳🇱' };
const MARKET_COLORS = {
  SE: { bg: '#f0f9ff', border: '#0284c7', text: '#0369a1' },
  NO: { bg: '#fff7ed', border: '#ea580c', text: '#9a3412' },
  FI: { bg: '#f0fdf4', border: '#16a34a', text: '#15803d' },
  DE: { bg: '#f5f3ff', border: '#7c3aed', text: '#6d28d9' },
  DK: { bg: '#fef2f2', border: '#dc2626', text: '#b91c1c' },
};

function CopyButton({ text, label, testId }) {
  const [copied, setCopied] = useState(false);
  const handle = async () => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success(`${label} kopierad!`);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error('Kunde inte kopiera');
    }
  };
  return (
    <button
      className={`fav-copy-btn${copied ? ' fav-copy-btn--done' : ''}`}
      onClick={handle}
      data-testid={testId}
      title={`Kopiera ${label}`}
    >
      {copied ? <Check size={12} /> : <Copy size={12} />}
      <span>{copied ? 'Kopierad' : label}</span>
    </button>
  );
}

// ── Edit modal ────────────────────────────────────────────────────────────────
function EditModal({ fav, onSave, onClose }) {
  const [output, setOutput]           = useState(fav.output || '');
  const [metaTitle, setMetaTitle]     = useState(fav.metaTitle || '');
  const [metaDesc, setMetaDesc]       = useState(fav.metaDescription || '');
  const [saving, setSaving]           = useState(false);

  const handleSave = async () => {
    setSaving(true);
    await onSave(fav.id, { output, metaTitle, metaDescription: metaDesc });
    setSaving(false);
    onClose();
    toast.success('Ändringar sparade!');
  };

  return (
    <div className="fav-modal-overlay" onClick={onClose}>
      <div className="fav-modal" onClick={e => e.stopPropagation()} data-testid="fav-edit-modal">
        <div className="fav-modal-header">
          <span className="fav-modal-title">Redigera — {fav.productName}</span>
          <button className="fav-modal-close" onClick={onClose} data-testid="fav-edit-modal-close">
            <X size={16} />
          </button>
        </div>

        <div className="fav-modal-body">
          <div className="fav-modal-field">
            <label className="fav-modal-label">Produktbeskrivning</label>
            <textarea
              className="fav-modal-textarea"
              value={output}
              onChange={e => setOutput(e.target.value)}
              rows={8}
              data-testid="fav-edit-output"
            />
          </div>
          <div className="fav-modal-field">
            <label className="fav-modal-label">
              Meta Title
              <span className={`fav-modal-char${metaTitle.length >= 50 && metaTitle.length <= 55 ? ' fav-modal-char--ok' : ' fav-modal-char--warn'}`}>
                {metaTitle.length} / 50–55
              </span>
            </label>
            <input
              className="fav-modal-input"
              value={metaTitle}
              onChange={e => setMetaTitle(e.target.value)}
              data-testid="fav-edit-meta-title"
            />
          </div>
          <div className="fav-modal-field">
            <label className="fav-modal-label">
              Meta Description
              <span className={`fav-modal-char${metaDesc.length >= 150 && metaDesc.length <= 160 ? ' fav-modal-char--ok' : ' fav-modal-char--warn'}`}>
                {metaDesc.length} / 150–160
              </span>
            </label>
            <textarea
              className="fav-modal-textarea fav-modal-textarea--short"
              value={metaDesc}
              onChange={e => setMetaDesc(e.target.value)}
              rows={3}
              data-testid="fav-edit-meta-desc"
            />
          </div>
        </div>

        <div className="fav-modal-footer">
          <button className="fav-modal-cancel" onClick={onClose} data-testid="fav-edit-cancel">
            Avbryt
          </button>
          <button
            className="fav-modal-save"
            onClick={handleSave}
            disabled={saving}
            data-testid="fav-edit-save"
          >
            {saving ? 'Sparar…' : 'Spara ändringar'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Favorite card ─────────────────────────────────────────────────────────────
function FavoriteCard({ fav, onDelete, onGenerateSimilar, onEdit }) {
  const [expanded, setExpanded] = useState(false);
  const marketStyle = MARKET_COLORS[fav.market] || {};
  const previewText = fav.output
    ? (expanded ? fav.output : fav.output.slice(0, 220) + (fav.output.length > 220 ? '...' : ''))
    : '—';

  return (
    <article className="fav-card" data-testid={`fav-card-${fav.id}`}>
      {/* Card header */}
      <div className="fav-card-header">
        <div className="fav-card-meta">
          {fav.market && (
            <span
              className="fav-market-badge"
              style={{ background: marketStyle.bg, color: marketStyle.text, borderColor: marketStyle.border }}
            >
              {MARKET_FLAGS[fav.market] || ''} {fav.market}
            </span>
          )}
          {fav.category && <span className="fav-cat-badge">{fav.category}</span>}
          {fav.wordCount > 0 && (
            <span className={`fav-wc-badge${fav.wordCount >= 150 && fav.wordCount <= 220 ? ' fav-wc-badge--ok' : ' fav-wc-badge--warn'}`}>
              {fav.wordCount} ord
            </span>
          )}
        </div>
        <div className="fav-card-actions-row">
          <button
            className="fav-edit-btn"
            onClick={() => onEdit(fav)}
            data-testid={`edit-fav-${fav.id}`}
            title="Redigera"
          >
            <Pencil size={13} />
          </button>
          <button
            className="fav-delete-btn"
            onClick={() => onDelete(fav.id)}
            data-testid={`delete-fav-${fav.id}`}
            title="Ta bort favorit"
          >
            <Trash2 size={13} />
          </button>
        </div>
      </div>

      {/* Product name */}
      <h3 className="fav-card-title" data-testid={`fav-title-${fav.id}`}>{fav.productName}</h3>

      {/* Description preview */}
      <p className="fav-card-desc">{previewText}</p>
      {fav.output && fav.output.length > 220 && (
        <button className="fav-expand-btn" onClick={() => setExpanded(v => !v)}>
          {expanded ? <><ChevronUp size={12} /> Visa mindre</> : <><ChevronDown size={12} /> Visa mer</>}
        </button>
      )}

      {/* Copy section */}
      <div className="fav-copy-section">
        <p className="fav-copy-label">Kopiera</p>
        <div className="fav-copy-row">
          <CopyButton text={fav.output}           label="Beskrivning" testId={`copy-desc-${fav.id}`} />
          <CopyButton text={fav.metaTitle}        label="Meta title"  testId={`copy-meta-title-${fav.id}`} />
          <CopyButton text={fav.metaDescription}  label="Meta desc."  testId={`copy-meta-desc-${fav.id}`} />
        </div>
      </div>

      {/* Generate similar */}
      <button
        className="fav-generate-btn"
        onClick={() => onGenerateSimilar(fav)}
        data-testid={`generate-similar-${fav.id}`}
      >
        <Sparkles size={13} />
        Generera liknande
      </button>
    </article>
  );
}

// ── Page ────────────────────────────────────────────────────────────────────────
export default function FavoritesPage({ onGenerateSimilar }) {
  const { favorites, removeFavorite, updateFavorite } = useData();
  const [filterMarket, setFilterMarket] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [search, setSearch] = useState('');
  const [editingFav, setEditingFav] = useState(null);

  const categories = useMemo(() => {
    const cats = [...new Set(favorites.map(f => f.category).filter(Boolean))];
    return cats.sort();
  }, [favorites]);

  const filtered = useMemo(() => {
    return favorites.filter(f => {
      if (filterMarket && f.market !== filterMarket) return false;
      if (filterCategory && f.category !== filterCategory) return false;
      if (search) {
        const q = search.toLowerCase();
        if (!f.productName?.toLowerCase().includes(q) && !f.output?.toLowerCase().includes(q)) return false;
      }
      return true;
    });
  }, [favorites, filterMarket, filterCategory, search]);

  const handleDelete = (id) => {
    removeFavorite(id);
    toast.success('Favorit borttagen');
  };

  const handleSaveEdit = async (id, updates) => {
    updateFavorite(id, updates);
  };

  const markets = ['SE', 'NO', 'FI', 'DE', 'DK', 'NL'];

  return (
    <div className="page-scroll" data-testid="favorites-page">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Favoriter</h1>
          <p className="page-subtitle">
            {favorites.length} sparad{favorites.length !== 1 ? 'e' : ''} beskrivning{favorites.length !== 1 ? 'ar' : ''}.
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="fav-filters" data-testid="fav-filters">
        <div className="fav-search-wrap">
          <Search size={14} className="fav-search-icon" />
          <input
            className="fav-search-input"
            placeholder="Sök på produktnamn eller text..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            data-testid="fav-search"
          />
        </div>

        <div className="fav-filter-chips">
          <button
            className={`fav-chip${filterMarket === '' ? ' fav-chip--active' : ''}`}
            onClick={() => setFilterMarket('')}
            data-testid="filter-all-markets"
          >
            <Globe size={11} /> Alla
          </button>
          {markets.map(m => (
            <button
              key={m}
              className={`fav-chip${filterMarket === m ? ' fav-chip--active' : ''}`}
              onClick={() => setFilterMarket(prev => prev === m ? '' : m)}
              data-testid={`filter-market-${m}`}
            >
              {MARKET_FLAGS[m]} {m}
            </button>
          ))}
        </div>

        {categories.length > 0 && (
          <div className="fav-cat-select-wrap">
            <select
              className="fav-cat-select"
              value={filterCategory}
              onChange={e => setFilterCategory(e.target.value)}
              data-testid="filter-category"
            >
              <option value="">Alla kategorier</option>
              {categories.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <ChevronDown size={13} className="fav-cat-select-icon" />
          </div>
        )}

        {(filterMarket || filterCategory || search) && (
          <button
            className="fav-clear-filters-btn"
            onClick={() => { setFilterMarket(''); setFilterCategory(''); setSearch(''); }}
            data-testid="clear-fav-filters"
          >
            Rensa filter
          </button>
        )}

        <span className="fav-result-count">{filtered.length} träffar</span>
      </div>

      {/* Grid */}
      {filtered.length === 0 ? (
        <div className="fav-empty" data-testid="fav-empty">
          <Heart size={40} className="fav-empty-icon" />
          <p className="fav-empty-title">
            {favorites.length === 0 ? 'Inga favoriter sparade ännu' : 'Inga favoriter matchar filtret'}
          </p>
          <p className="fav-empty-desc">
            {favorites.length === 0
              ? 'Generera en beskrivning och klicka på hjärtat för att spara den här.'
              : 'Prova att ändra dina filter.'}
          </p>
        </div>
      ) : (
        <div className="fav-grid" data-testid="fav-grid">
          {filtered.map(fav => (
            <FavoriteCard
              key={fav.id}
              fav={fav}
              onDelete={handleDelete}
              onGenerateSimilar={onGenerateSimilar}
              onEdit={setEditingFav}
            />
          ))}
        </div>
      )}

      {/* Edit modal */}
      {editingFav && (
        <EditModal
          fav={editingFav}
          onSave={handleSaveEdit}
          onClose={() => setEditingFav(null)}
        />
      )}
    </div>
  );
}
